import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  IAdminUser,
  ICustomerUser,
  type ILabReq,
  type ISelectOption,
  type LabReqTestOption
} from '@care-portals/api-interfaces';
import { qcIndividualFields, qcProfileFields } from '@care-portals/data';
import {
  biochemistryFields,
  hematologyFields,
  immunologyFields,
  microbiologyFields,
  otherTestsFields,
  psaFields,
  viralFields,
  vitaminFields
} from '@care-portals/data';
import { getLicenseForProvince } from '../../lab-req.utils';

import { Subject } from 'rxjs';
import { InsuranceType } from '@care-portals/data';

@Component({
  selector: 'lab-req-base',
  template: '<div><div>'
})
export abstract class LabReqBaseComponent implements OnInit, OnDestroy {
  @Input() labReq: ILabReq;
  @Input() title: string;
  values: { [key: string]: string | number };
  adminUser: IAdminUser;
  brand: string;
  user: ICustomerUser;
  stop$ = new Subject();
  today = new Date();
  signatureUrl: string;
  createdAt: Date;
  title1 = 'Prostate Specific Antigen (PSA)';
  fields1: LabReqTestOption[] = [
    { type: 'boolean', id: 'totalPSA', label: 'Total PSA' },
    { type: 'boolean', id: 'freePSA', label: 'Free PSA' },
    {
      type: 'boolean',
      id: 'insured',
      label: 'Insured – Meets OHIP eligibility criteria'
    },
    {
      type: 'boolean',
      id: 'uninsured',
      label: 'Uninsured – Screening: Patient responsible for payment'
    }
  ];

  title2 = 'Biochemistry';
  fields2: LabReqTestOption[] = [
    { type: 'boolean', id: 'hbA1C', label: 'HbA1C' },
    { type: 'boolean', id: 'alt', label: 'ALT' },
    { type: 'boolean', id: 'alk', label: 'Alk. Phosphatase' },
    {
      type: 'boolean',
      id: 'lipid',
      label:
        'Lipid Assessment (includes Cholesterol, HDL-C, Triglycerides, calculated LDL-C & Chol/HDL-C ratio; individual lipid tests may be ordered in the “Other Tests” section of this form)'
    }
  ];

  title3 = 'Urine Tests';
  fields3: LabReqTestOption[] = [
    {
      type: 'boolean',
      id: 'macroscopic2Microscopic',
      label: 'Macroscopic -> microscopic if dipstick positive'
    },
    {
      type: 'boolean',
      id: 'macroscopic2Urine',
      label: 'Macroscopic -> urine culture if pyuria or nitrite present'
    },
    {
      type: 'boolean',
      id: 'macroscopicDipstick',
      label: 'Macroscopic (dipstick)'
    },
    { type: 'boolean', id: 'microscopic', label: 'Microscopic' },
    {
      type: 'string',
      id: 'clinicalInfo',
      label: 'Clinical information ofr microscopic required'
    }
  ];

  constructor() { }

  ngOnInit(): void {
    if (this.labReq) {
      this.values = this.labReq.data;
      this.adminUser = this.labReq.adminUser as IAdminUser;
      this.signatureUrl = `assets/signatures/${this.adminUser._id}.png`;
      this.createdAt = this.labReq.createdAt;

      this.user = this.labReq.customer as ICustomerUser;
      this.brand = this.labReq.brand;
    }
  }

  ngOnDestroy(): void {
    this.stop$.next(true);
    this.stop$.complete();
  }
}

@Component({
  selector: 'lab-req-qc',
  styleUrls: ['QC.less'],
  templateUrl: './QC.html'
})
export class LabReqQC extends LabReqBaseComponent {
  fields1 = qcProfileFields.fields1;
  fields2 = qcProfileFields.fields2;
  fields3 = qcIndividualFields.fields1;
  fields4 = qcIndividualFields.fields2;
  fields5 = qcIndividualFields.fieldsTestSpec;
  fields6 = qcIndividualFields.fieldsSpecSource;
  headerStyle =
    'color: rgb(131, 190, 55); font-size: 16px; border-bottom: solid 2px #8080805c;';
  inputColor = 'rgb(131, 190, 55)';
}

@Component({
  selector: 'lab-req-on',
  styleUrls: ['ON.less'],
  templateUrl: './ON.html'
})
export class LabReqON extends LabReqBaseComponent {
  fields1 = biochemistryFields;
  fields21 = hematologyFields;
  fields22 = immunologyFields;
  fields23 = microbiologyFields;
  fields31 = viralFields;
  fields32 = psaFields;
  fields33 = vitaminFields;
  fields34 = otherTestsFields;
  headerStyle =
    'background: rgb(211, 211, 211); color: black; font-size: 14px;margin-bottom: 0;';
  inputColor = 'black';
  insuranceTypeOptions: Array<ISelectOption> = [
    { value: InsuranceType.OHIP, label: 'OHIP/Insured' },
    { value: InsuranceType.ThirdParty, label: 'Third Party / Uninsured' },
    { value: InsuranceType.WSIB, label: 'WSIB' }
  ];

  get otherTestsValues() {
    return this.values['otherTests_test']
      ? Object.keys(this.values['otherTests_test']).map(
        key => this.values['otherTests_test'][key]
      )
      : [];
  }

  get selectedLicense() {
    return getLicenseForProvince(this.adminUser?.licenses as any[], 'ON');
  }
}

@Component({
  selector: 'lab-req-ab',
  templateUrl: './AB.html'
})
export class LabReqAB extends LabReqBaseComponent {
  headerStyle =
    'background: rgb(211, 211, 211); color: black; font-size: 16px;';
  inputColor = 'black';
}

@Component({
  selector: 'lab-req-bc',
  templateUrl: './BC.html'
})
export class LabReqBC extends LabReqBaseComponent {
  headerStyle =
    'background: rgb(46, 66, 121); color: white; font-size: 18px; text-transform: uppercase;';
  inputColor = 'rgb(46, 66, 121)';
}
